/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.14.0
 */

import type { ChangeEvent } from 'react';
import { Fragment, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';

import { useInfiniteQuery } from '@/main/api/use-infinite-query';
import { Input, LinkBlock, Select, TooltipModal } from '@components/common';
import { DropDownFilterButtonSize } from '@components/common/DropDownFilter/DropDownFilter';
import { EInputKind } from '@components/common/Input/Input';
import type { IOptionItemProps } from '@components/common/Select/Select';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { useModalOpenError } from '@modules/modal/hooks/useModalOpenError';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';

import { useSubscriberIdContext } from '../../../context';
import {
  manageNumberIsPositionChangeAvailableSelector,
  manageNumberPositionSelectedSelector,
  manageNumberTranslationSelector,
} from '../selectors';
import {
  manageNumberApi,
  useCreateManegeNumberPositionMutation,
  useUpdateManageNumberPositionMutation,
} from '../store';
import type { IManageNumberPositionQuery } from '../types';
import { ManageNumberPositionStyled } from './styled/ManageNumberStyled';

const ManageNumberPosition = () => {
  const { subscriberId } = useSubscriberIdContext();

  /* Select State */

  const translationCommon = useSelector(makeSelectTranslationCommon());
  const translationManageNumber = useSelector(
    manageNumberTranslationSelector(),
  );

  /* Api */

  const positionSelectFromMeta = useSelector(
    manageNumberPositionSelectedSelector(subscriberId),
  );
  const isPositionChangeAvailable = useSelector(
    manageNumberIsPositionChangeAvailableSelector(subscriberId),
  );

  const { data, isLoading, fetchNextPage, hasMore } =
    useInfiniteQuery<IOptionItemProps>(
      manageNumberApi.endpoints.getManageNumberPositionList,
    );

  const emptyPosition = {
    value: '',
    label: translationManageNumber.position.select.empty,
  };

  const [createPosition] = useCreateManegeNumberPositionMutation();
  const [updatePosition] = useUpdateManageNumberPositionMutation();

  /* Local State */

  const [queryParams, setQueryParams] = useState<IManageNumberPositionQuery>(
    {},
  );

  const [newPosition, setNewPosition] = useState<string>('');
  const [searchPosition, setSearchPosition] = useState('');
  const [currentPosition, setCurrentPosition] =
    useState<IOptionItemProps | undefined>();

  const { open: openModalError } = useModalOpenError();

  /* Lifecycle e */

  useEffect(() => {
    fetchNextPage(queryParams);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  useEffect(() => {
    setCurrentPosition(positionSelectFromMeta);
  }, [positionSelectFromMeta]);

  /* Handlers */

  const delayedStartSearch = debounce((searchValue: string) => {
    setQueryParams({ searchValue, start: true });
  }, DEBOUNCE_DEFAULT_DELAY);

  const handleSearchChange = (searchValue: string) => {
    setSearchPosition(searchValue);
    delayedStartSearch(searchValue);
  };

  const onSelectHandler = (position: IOptionItemProps) => {
    setCurrentPosition(position);
    if (searchPosition) {
      setQueryParams({ searchValue: '', start: true });
      setSearchPosition('');
    }
  };

  const onCancelPosition = () => {
    setCurrentPosition(positionSelectFromMeta);
  };

  const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    const value = event.target.value;
    setNewPosition(value);
  };

  const onUpdatePosition = () => {
    currentPosition &&
      updatePosition({
        subscriberId,
        positionId: currentPosition.value.toString(),
      });
  };

  const onCreatePosition = () => {
    setNewPosition('');
    createPosition({ name: newPosition, subscriberId })
      .unwrap()
      .then((data) => {
        console.log('error data', data);
        if (data) {
          setCurrentPosition({ label: data.name, value: data.id });
        }
      })
      .catch((e) => {
        const title =
          translationCommon.error.codes[e?.code] ||
          translationCommon.error.defaultMessage;
        openModalError('', title);
      });
  };

  if (!isPositionChangeAvailable || !data) {
    if (!positionSelectFromMeta?.label) return null;

    return (
      <ManageNumberPositionStyled>
        <div className="subscriber-id__title subscriber-id__title_bold">
          {translationManageNumber.position.title}
        </div>
        {positionSelectFromMeta.label}
      </ManageNumberPositionStyled>
    );
  }

  return (
    <ManageNumberPositionStyled>
      <div className="subscriber-id__title subscriber-id__title_bold">
        {translationManageNumber.position.title}
      </div>

      <div className="manage-number__position-container">
        <Select
          dataName="manage-number-position-select"
          filterButtonSize={DropDownFilterButtonSize.medium}
          kind={EInputKind.normal}
          options={[emptyPosition, ...data] || []}
          placeholder={translationManageNumber.position.select.placeholder}
          searchProps={{
            onSearchChange: handleSearchChange,
            searchValue: searchPosition,
          }}
          selectedOption={currentPosition}
          translation={translationCommon}
          virtualListProps={{
            isLoading: isLoading,
            hasMore,
            onScrollEvent: () =>
              setQueryParams({ searchValue: searchPosition, start: false }),
            rowHeight: 32,
          }}
          onSelect={onSelectHandler}
        />
        <TooltipModal>
          {({ onClose, onToggle }) => (
            <Fragment>
              <LinkBlock
                dataName="manage-number-position-create"
                onClick={onToggle}
              >
                {translationManageNumber.position.create.open}
              </LinkBlock>
              <TooltipModal.Body>
                <div className="manage-number__position-modal">
                  <Input
                    autoFocus
                    className="position-modal__input"
                    dataName="manage-number-position-create-input"
                    maxLength={255}
                    placeholder={
                      translationManageNumber.position.create.placeholder
                    }
                    value={newPosition}
                    onChange={inputChangeHandler}
                  />
                  <ButtonServiceUserDenied
                    className="position-modal__btn"
                    dataName="manage-number-position-create-btn"
                    disabled={!newPosition}
                    kind="mediumNormal"
                    onClick={() => {
                      if (newPosition) {
                        onClose();
                        onCreatePosition();
                      }
                    }}
                  >
                    {translationManageNumber.position.create.btn}
                  </ButtonServiceUserDenied>
                </div>
              </TooltipModal.Body>
            </Fragment>
          )}
        </TooltipModal>
      </div>

      <div className="manage-number__position-control">
        <ButtonServiceUserDenied
          className="manage-number__position-save"
          dataName="manage-number-edit-labels"
          disabled={currentPosition?.value === positionSelectFromMeta?.value}
          kind="mediumNormal"
          onClick={onUpdatePosition}
        >
          {translationCommon.form.save}
        </ButtonServiceUserDenied>

        <LinkBlock
          disabled={currentPosition?.value === positionSelectFromMeta?.value}
          onClick={onCancelPosition}
        >
          {translationCommon.form.cancel}
        </LinkBlock>
      </div>
    </ManageNumberPositionStyled>
  );
};

export default ManageNumberPosition;
